import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api, qs } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Champ, Panneau, Squelette, Vide } from '../components/ui';
import { FormulaireClient } from '../components/FormulaireClient';
import { dateFr, montant, nomClient } from '../lib/format';
import type { Client, Reservation, Verdict } from '../api/types';

export default function Clients() {
  const { peut } = useAuth();
  const [q, setQ] = useState('');
  const [liste, setListe] = useState<Client[] | null>(null);
  const [detail, setDetail] = useState<{ client: Client; verdict: Verdict; reservations: Reservation[] } | null>(null);
  const [nouveau, setNouveau] = useState(false);
  const [edite, setEdite] = useState<Client | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { api.get<Client[]>(`/clients${qs({ q })}`).then(setListe); }, 220);
    return () => clearTimeout(t);
  }, [q]);

  if (!liste) return <Squelette lignes={8} />;

  return (
    <>
      <div className="entete">
        <div className="titres"><h1>Clients</h1><p>{liste.length === 0 && !q
          ? 'Chaque location part d\'une fiche client'
          : `${liste.length} fiche(s)`}</p></div>
        <div className="actions"><button className="btn btn-p" onClick={() => setNouveau(true)}>Nouveau client</button></div>
      </div>

      <Carte>
        <Champ label="Rechercher par nom ou téléphone">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Bouzidi, 0661…" />
        </Champ>
      </Carte>

      <div style={{ marginTop: 16 }}>
        <Carte pad={false}>
          {liste.length === 0 ? (
            <div className="carte-p">
              <Vide titre={q ? 'Aucun client ne correspond' : 'Aucune fiche client'}
                    action={!q ? <button className="btn btn-p btn-l" onClick={() => setNouveau(true)}>Créer ma première fiche</button> : undefined}>
                {q
                  ? <>Essayez avec une autre orthographe, ou seulement les premiers chiffres du téléphone.</>
                  : <>La fiche porte la date de naissance et la date du permis : ce sont elles qui
                     déclenchent les blocages de l'article 01 — 28 ans minimum, permis de 5 ans.
                     Elle se crée aussi directement depuis une nouvelle location.</>}
              </Vide>
            </div>
          ) : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead><tr><th>Nom</th><th>Téléphone</th><th>Pièce</th><th>Permis délivré le</th><th /></tr></thead>
              <tbody>
                {liste.map((c) => (
                  <tr key={c._id} style={{ cursor: 'pointer' }}
                      onClick={() => api.get<typeof detail>(`/clients/${c._id}`).then(setDetail)}>
                    <td>
                      <b>{nomClient(c)}</b>
                      {c.type === 'societe' && <span style={{ marginLeft: 6 }}><Badge statut="confirmee" libelle="Société" /></span>}
                      {c.listeNoire && <span style={{ marginLeft: 6 }}><Badge statut="retard" libelle="Liste noire" /></span>}
                      {c.compteATerme && <span style={{ marginLeft: 6 }}><Badge statut="retour" libelle="Compte a terme" /></span>}
                    </td>
                    <td className="num">{c.telephone}</td>
                    <td className="mini sourdine">{c.pieceType} {c.pieceNumero}</td>
                    <td>{dateFr(c.permisDelivreLe)}</td>
                    <td className="droite"><span className="btn btn-s">Ouvrir</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </Carte>
      </div>

      {edite && (
        <FormulaireClient client={edite}
          onFermer={() => setEdite(null)}
          onCree={(c, v) => {
            setEdite(null);
            setDetail(detail ? { ...detail, client: c, verdict: v } : null);
            api.get<Client[]>(`/clients${qs({ q })}`).then(setListe);
          }} />
      )}

      {nouveau && (
        <FormulaireClient onFermer={() => setNouveau(false)}
                          onCree={(c, v) => { setNouveau(false); setDetail({ client: c, verdict: v, reservations: [] }); setQ(c.nom); }} />
      )}

      {detail && (
        <Panneau titre={nomClient(detail.client)} onFermer={() => setDetail(null)}>
          {detail.client.listeNoire && <Bandeau type="ko" titre="Client en liste noire">{detail.client.motifListeNoire}</Bandeau>}

          {/*
            * Une date de naissance mal saisie bloque le client a l'article 01 a
            * chaque location, pour toujours. La demande de correction pouvait
            * etre accordee — mais rien ne permettait ensuite de changer la
            * valeur : la fiche etait en lecture seule partout.
            */}
          {peut('modifier.client') && (
            <div className="actions" style={{ marginTop: 12 }}>
              <button className="btn" onClick={() => setEdite(detail.client)}>Modifier la fiche</button>
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <Carte titre="Article 01">
              {detail.verdict.conforme ? (
                <Bandeau type="ok" titre="Conforme">
                  {detail.verdict.age} ans · permis depuis {detail.verdict.ancienneteParmis} ans.
                </Bandeau>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {detail.verdict.blocages.map((b) => (
                    <Bandeau key={b.regle} type="ko" titre={b.message}>{b.detail}</Bandeau>
                  ))}
                </div>
              )}
            </Carte>
          </div>

          <div style={{ marginTop: 14 }}>
            <Carte titre="Coordonnées">
              <table className="tbl compact">
                <tbody>
                  <tr><td>Téléphone</td><td className="droite num">{detail.client.telephone}</td></tr>
                  <tr><td>E-mail</td><td className="droite">{detail.client.email ?? '—'}</td></tr>
                  <tr><td>Adresse</td><td className="droite">{detail.client.adresse ?? '—'}</td></tr>
                  <tr><td>Naissance</td><td className="droite">{dateFr(detail.client.dateNaissance)} {detail.client.lieuNaissance && `a ${detail.client.lieuNaissance}`}</td></tr>
                  <tr><td>{detail.client.pieceType}</td><td className="droite num">{detail.client.pieceNumero}</td></tr>
                  <tr><td>Permis</td><td className="droite">{detail.client.permisNumero ?? '—'} · {dateFr(detail.client.permisDelivreLe)}</td></tr>
                </tbody>
              </table>
            </Carte>
          </div>

          <div style={{ marginTop: 14 }}>
            <Carte titre={`Historique — ${detail.reservations.length} location(s)`} pad={false}>
              <table className="tbl compact">
                <thead><tr><th>N°</th><th>Véhicule</th><th>Période</th><th className="droite">Total</th><th /></tr></thead>
                <tbody>
                  {detail.reservations.map((r) => {
                    const v = r.vehicle as { marque?: string; modele?: string };
                    return (
                      <tr key={r._id}>
                        <td><Link to={`/reservations/${r._id}`}><b>{r.numero}</b></Link></td>
                        <td>{v?.marque} {v?.modele}</td>
                        <td className="mini sourdine">{dateFr(r.dateDebut)} → {dateFr(r.dateFin)}</td>
                        <td className="droite montant">{montant(r.totalPrevuMinor)}</td>
                        <td><Badge statut={r.statut} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Carte>
          </div>
        </Panneau>
      )}
    </>
  );
}
